import { defer as observableDefer,  BehaviorSubject, Observable, of } from 'rxjs';
import { publishReplay, refCount, take, tap, mergeMap } from 'rxjs/operators';
import { EventService } from './event.service';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { CategoryStreamsRequestModel, StreamItemRequestModel, UserStreamsRequestModel } from '../model';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { UtilService } from './util.service';
import * as moment from 'moment';
import { TenantService } from './tenant.service';

export interface BasicFilter {
    isAdvanced: boolean;
    basicFilterText: string;
    matchType: string;
    basicFilterStructures: BasicFilterStructure[];
}

export interface TagFilter {
    key: string;
    tagText: string; 
    type: string;
}

export interface BasicFilterStructure {
    condition: string; 
    type: string;
    values: TagFilter[];
}

@Injectable({ providedIn: 'root' })
export class StreamService {
    public static readonly types = {
        PRODUCT_SOURCE: { label: 'Product Source', set: 'fa', icon: 'fa-newspaper' },
        RSS: { label: 'RSS', set: 'fa', icon: 'fa-rss'},
        COLLECTION: { label: 'Topic', set: 'fa', icon: 'fa-sitemap' },
        BING_SEARCH: { label: 'Bing', set: 'fab', icon: 'fa-windows' },
        PUBMED_SEARCH: { label: 'PubMed', set: 'fa', icon: 'fa-code' },
        SCOPUS_SEARCH: { label: 'Scopus', set: 'fa', icon: 'fa-code' },
        TWITTER_SEARCH: { label: 'Twitter', set: 'fab', icon: 'fa-twitter' },
        ATTENSA_FTP: { label: 'XML', set: 'fa', icon: 'fa-code' },
        DIFFBOT_SEARCH: { label: 'Diffbot Knowledge Graph', set: 'fa', icon: 'fa-search' },
        ATTENSA_SEARCH: { label: 'Attensa Search', set: 'fa', icon: 'fa-search' },
        CLINICALTRIALS_SEARCH: { label: 'Clinical Trials', set: 'fa', icon: 'fa-code' },
        FACTIVA_SELECT_HTTP: { label: 'Factiva Select', set: 'fa', icon: 'fa-code' },
        BRICK_FTP: { label: 'XML', set: 'fa', icon: 'fa-code' },
    };
    path: string = '/ws/streams';
    uaPath: string = '/ws/ua/streams';
    streamUpdates = new BehaviorSubject<any>(null);
    streamMap = {};
    productSourceMap = {};

    constructor(private api: ApiService,
                private eventService: EventService, 
                private tenantService: TenantService,
                private utilService: UtilService, 
                private snackBar: MatSnackBar) {}

    getUserStreams(request: UserStreamsRequestModel) {
        return this.api.get('/ws/user/streams', request.asURLSearchParams());
    }
    
    getStream(streamId: string, skipCache = false): Observable<any> {
        if (skipCache || !this.streamMap[streamId]) {
            this.streamMap[streamId] = observableDefer(() => this.api.get(`${this.uaPath}/${streamId}`)).pipe(publishReplay(1, 5000), refCount(), take(1));
        }
        return this.streamMap[streamId];
    }

    getStreamItems(streamId: string, request: StreamItemRequestModel) {
        return this.api.get(`${this.uaPath}/${streamId}/items`, request.asURLSearchParams());
    }

    getStreamCurationItems(streamId: string, request: StreamItemRequestModel) {
        return this.api.get(`${this.path}/${streamId}/curationItems`, request.asURLSearchParams())
    }

    getTenantStreams(params: {
        page?: number, 
        rows?: number, 
        includeCatalogs?: boolean, 
        published?: boolean,
        featured?: boolean,
        ownerId?: string,
        sort?: string, 
        sortDirection?: string,
        term?: string,
        type?: string,
        createdAfterDateTime?: string,
        excludeTypes? : Array<string>}): Observable<any> { 
        return this.api.get(`${this.uaPath}/tenant`, this.api.paramify(params));
    }
    getSubtenantStreams(subtenantId, params):Observable<any> {
        return this.api.get(`${this.path}/subtenant/${subtenantId}`, this.api.paramify(params));
    }
    
    getCategoryStreams(request: CategoryStreamsRequestModel):Observable<any> {
        return this.api.get('/ws/ua/taxonomy/streams', request.asURLSearchParams());
    }

    streamUpdated(stream) {
        return this.streamUpdates.next(stream);
    }

    getStreamFollowers(streamId, params) {
        return this.api.get(`${this.path}/${streamId}/followers`, this.api.paramify(params));
    }

    publicSubscribe(topicId: string, topicTitle: string, emailAddress: string) {
        return this.api.post('/ws/ua/user/public/streams', { topicId, topicTitle, emailAddress }); 
    }

    setSubscription(streamId: string,
                       userId: string,
                       subscribedToStream: boolean,
                       subscribedToBriefing: boolean = false,
                       alert: boolean = true) {
        return this.api.post(`/ws/user/${userId}/streams`, {
            streamId: streamId,
            follow: subscribedToStream,
            subscribeToBriefing: subscribedToBriefing,
            restricted: false
        }, { responseType: 'text' })
        .pipe(
            tap(() => alert ? this.snackBar.open(subscribedToStream ? 'Subscribed to topic.' : 'Unsubscribed from topic.', 'OK', { duration: 2000 }) : ''),
            tap(() => {
                if (subscribedToStream) {
                    this.eventService.postEvent('followed', { streamId })
                }
            })
        );
    }

    getProductSource = (streamId: string): Observable<any> => {
        if (!this.productSourceMap[streamId]) {
            this.productSourceMap[streamId] = observableDefer(() => this.api.get(`${this.path}/${streamId}/productSource`))
            .pipe(
                publishReplay(1, 60000),
                refCount(),
                take(1)
            ); 
        }
        return this.productSourceMap[streamId];
    }

    addStreamRole(userId, streamId, roleType) {
        return this.api.post(`/ws/user/${userId}/streamRoles`, { params: { streamId, roleType } });
    }

    deleteStreamRole(userId, streamId, roleType) {
        return this.api.delete(`/ws/user/${userId}/streamRoles`, { body: { params: { streamId, roleType } } });
    }

    saveSubtenantStream(tenantId, stream) {
        return this.api.post(`/ws/streams/subtenant/${tenantId}`, stream);
    }

    subscribeStream(streamId, targetStreamId, tenantId?) {
        return this.api.post(`/ws/streams/${streamId}/subscriptions`, [targetStreamId], { params: this.api.paramify({ tenantId }) });
    }

    deleteSubtenantStream(tenantId, streamId) {
        return this.api.delete(`/ws/streams/subtenant/${tenantId}/${streamId}`, { responseType: 'text' });
    }

    updateSubtenantStream(stream) {
        return this.api.put(`/ws/streams/subtenant/${stream.tenantId}/${stream.id}`, stream);
    }

    saveCatalogStream(stream, params?) {
        return this.api.post('/ws/streams/shared', stream, { params });
    } 

    saveStream(stream, params?) {
        return this.tenantService.isAutoPublishTopicTenant()
        .pipe(
            mergeMap(isAutoPublishTopic => {
                if(isAutoPublishTopic && stream.type === 'COLLECTION'){
                    stream.published = true;
                    this.eventService.postEvent('topic_created');
                }else{
                    this.eventService.postEvent('source_created');
                }
                if(stream.type === 'TWITTER_SEARCH' || stream.type === 'BING_SEARCH' || stream.type === 'ATTENSA_SEARCH') {
                    stream.settings = stream.settings || {};
                    stream.settings.itemTimeToLiveInDays = 30;
                }else if(stream.type !== 'COLLECTION'){
                    stream.settings = stream.settings || {};                
                    stream.settings.itemTimeToLiveInDays = 90;
                }
                return this.api.post('/ws/streams', stream, { params: params });
            })
        );
    }

    saveImage(streamId, image) {
        const formData = new FormData();
        formData.append('file', image);
        return this.api.post(`/ws/streams/${streamId}/image`, formData);
    };

    updateStream(stream) {
        return this.api.put(`/ws/streams/${stream.id}`, stream);
    }

    postStreamSubscriptions(streamId, targetStreamIds) {
        return this.api.post(`/ws/streams/${streamId}/subscriptions`, targetStreamIds);
    }

    saveItem(streamId, item) {
        return this.api.post(`/ws/item?streamId=${streamId}`, item);
    }

    deleteStream(streamId) {
        return this.api.delete(`/ws/streams/${streamId}`, { responseType: 'text' });
    }

    deleteStreamSubscription(streamId, targetStreamId) {
        return this.api.delete(`/ws/streams/${streamId}/subscriptions/${targetStreamId}`, { responseType: 'text' });
    }

    createStream(stream) {
        return this.tenantService.isAutoPublishTopicTenant()
        .pipe(
            mergeMap(isAutoPublishTopic => {
                if(isAutoPublishTopic && stream.type === 'COLLECTION'){
                    stream.published = true;
                    this.eventService.postEvent('topic_created');
                }else{
                    this.eventService.postEvent('source_created');
                }

                if(stream.type === 'TWITTER_SEARCH' || 
                stream.type === 'BING_SEARCH' ||
                stream.type === 'ATTENSA_SEARCH') {
                    stream.settings = stream.settings || {};
                    stream.settings.itemTimeToLiveInDays = 30;
                }else if(stream.type !== 'COLLECTION'){
                    stream.settings = stream.settings || {};                
                    stream.settings.itemTimeToLiveInDays = 90;
                }
                return this.api.post('/ws/streams', stream);
            })
        );
    }

    postFilter(streamId, filter) {
        return this.api.post(`/ws/streams/${streamId}/filter`, filter);
    }

    deleteFilter(streamId, filterId) {
        return this.api.delete(`/ws/streams/${streamId}/filter/${filterId}`);
    }

    addStreamsToCatalog(catalogId: string, streamIds: string[]) {
        return this.api.post(`/ws/catalogs/${catalogId}/streams`, streamIds);
    }

    getSharedStreams(options) {
        return this.api.get('/ws/streams/shared', this.api.paramify(options));
    }

    getSharedRecommendedStreams(options) {
        return this.api.get('/ws/streams/shared/recommended', this.api.paramify(options));
    }

    getRecommendedStreams(options) {
        return this.api.get('/ws/streams/recommended/search', this.api.paramify(options));
    }

    addStreamToCatalogCategory(catalogId: string, categoryId: string, streamId: string) {
        return this.api.post(`/ws/catalogs/${catalogId}/taxonomy/${categoryId}/streams`, [streamId]);
    }

    deleteStreamFromCatalogCategory(catalogId: string, categoryId: string, streamId: string): Observable<any> {
        return this.api.delete(`/ws/catalogs/${catalogId}/taxonomy/${categoryId}/streams/${streamId}`);
    }

    getCatalogStream(catalogId, streamId): Observable<any> {
        return this.api.get(`/ws/catalogs/${catalogId}/streams/${streamId}`);
    }

    getCatalogStreams(catalogId, options) {
        return this.api.get(`/ws/ua/streams/catalog/${catalogId}/search`, this.api.paramify(options));
    }

    getCatalogCategoryStreams(catalogId, options) {
        return this.api.get(`/ws/ua/taxonomy/catalog/${catalogId}/streams`, options);
    }

    getSourceCounts(streamId) { 
        return this.api.get(`/ws/topics/${streamId}/count`);
    }

    getSourceTopics(streamId, params) {
        return this.api.get(`/ws/streams/${streamId}/topics`, this.api.paramify(params));
    }

    getSubstreams(streamId, params) {
        return this.api.get(`/ws/streams/${streamId}/substreams`, this.api.paramify(params));
    }

    tenantStreamSearch(options) {
        return this.api.get('/ws/streams/tenant/search', this.api.paramify(options));
    }

    textIsLuceney(term) {
        return term &&
            (term.includes('(') ||
                term.includes(')') ||
                term.includes('AND') ||
                term.includes('OR') ||
                term.includes(':') ||
                term.includes('NOT') ||
                term.includes('|') ||
                term.includes('&&'));
    }

    needsWrapping(item: string): boolean {
        return item.includes(' ') && !(item.startsWith('"') && item.endsWith('"'));
    }

    expandFilterTerm(filterTerm, and = true) {
        const parts = [];
        let term = filterTerm;
        
        if (term) { 
            while (term.match(/"(.*?)"/)) {
                const extraction = term.match(/"(.*?)"/);
                parts.push(extraction[0]);
                term = term.replace(extraction[0], '');
            }

            let endPart;
            if (term.includes(',')) {
                const splitParts = parts.concat(term.split(',')).map(item => item.trim()).filter(item => !!item.length);
                endPart = splitParts.map(chunk => this.needsWrapping(chunk) ? `"${chunk}"` : chunk);
            } else {
                endPart = parts.concat(term.split(' ')).map(item => item.trim());
            }
            endPart = endPart.filter(part => part && part.trim().length);
            return '(' + endPart.join(`) ${and ? 'AND' : 'OR'} (`) + ')';
        }
        return term;
    }

    basicFilterTermFromFilter(filter: BasicFilter): string {
        if (filter.basicFilterText) {
            let filterTerm = filter.basicFilterText;
            if (!this.textIsLuceney(filter.basicFilterText)) {
                filterTerm = filter.matchType === 'MatchExactPhrase' ?
                '"' + (filter.basicFilterText || '').replace(new RegExp('"', 'g'), '\\"') + '"' :
                this.expandFilterTerm(this.utilService.cleanQuotes(filter.basicFilterText), filter.matchType === 'IncludeAllKeywords');
            }
            return `${filterTerm.trim() === '(()) AND' ? '' : filterTerm.trim()}`;
        } else {
            return '';
        }
    }

    fetchBriefingStreams(briefingId) {
        return this.api.get(`/ws/briefing/${briefingId}/streams`, this.api.paramify({ rows: 500 }));
    }

    topicHasNewContent(topic, userSubscription): boolean {
        if (!topic.itemsCount) {
            return false;
        } else if (!userSubscription.lastOpenedDate) {
            return true;
        } else {
            return moment(userSubscription.lastOpenedDate).isBefore(moment(topic.itemsLastAddedDate));
        }
    }

    getRssPreview(config: {uri, username?, password? }, proxyId?): Observable<any> {
        return this.api.post('/ws/stream_source/rss/preview', config, { params: this.api.paramify({ proxyId })});
    }

    addImages(imageMap) {
        return this.api.post('/ws/streams/images', imageMap, { responseType: 'text' });
    }

    fetchStreamGroups(streamId, params) {
        return this.api.get(`/ws/streams/${streamId}/groups`, this.api.paramify(params));
    }
}
